// src/constants/mapLayerIds.js
//
// MapLibre layer ids for the product feature layers, hoisted out of the
// components that add them so MapView can reason about click priority
// without hand-copying id strings that would silently drift.
//
// Each owning component imports its own group back under its original local
// name (LAYER / LAYERS), so call sites inside those files are unchanged.
// They live here rather than being exported from the components themselves
// because a component module that also exports constants breaks React Fast
// Refresh (react-refresh/only-export-components).

// WellFinder detections + match visuals — components/wellfinder/WellLayer.jsx
export const WELLFINDER_LAYERS = {
  DETECTED_HALO:         "detected-well-halo",
  RRC_HALO:              "rrc-well-halo",
  DETECTED_CORE:         "detected-well-core",
  DETECTED_RING:         "detected-well-ring",
  DETECTED_NO_WELL_HALO: "detected-well-no-well-halo",
  DETECTED_NO_WELL:      "detected-well-no-well",
  OFFSET_LINE:           "well-offset-line",
  OFFSET_SELECTED:       "well-offset-line-selected",
};

// Well pads — components/padrekko/PadLayer.jsx (renders in every product)
export const PAD_LAYERS = {
  FILL:     "pads-fill",
  OUTLINE:  "pads-outline",
  HOVER:    "pads-hover",
  SELECTED: "pads-selected",
};

// SiteID sites + clusters — components/siteid/SiteLayer.jsx
export const SITEID_LAYERS = {
  CLUSTERS:         "siteid-clusters",
  CLUSTER_COUNT:    "siteid-clusters-count",
  POLYGONS:         "siteid-polygons-v2",
  POLYGONS_OUTLINE: "siteid-polygons-outline-v2",
};

// RRC wells — added by MapView's own registerWellLayers(), not a component.
export const RRC_WELL_POINTS = "rrc-well-points";

// ---------------------------------------------------------------------------
// Click priority
// ---------------------------------------------------------------------------
// Feature layers that OWN a click landing on them, in preference to the AOI
// polygon underneath.
//
// MapLibre's delegated listeners (map.on('click', layerId, fn)) have no
// stacking arbitration: each one independently queries its own layer at the
// click point and fires if it hits, regardless of what is drawn on top. So a
// click on a well dot inside an AOI fires BOTH the well handler and the
// aoi-base-fill handler — and the AOI one, registered first, synchronously
// fitBounds() the camera out to the AOI before the well focus can run.
//
// Add a layer here when it is drawn above aoi-base-fill AND owns a click.
export const CLICK_PRIORITY_LAYERS = [
  RRC_WELL_POINTS,
  WELLFINDER_LAYERS.DETECTED_CORE,
  WELLFINDER_LAYERS.DETECTED_NO_WELL,
  PAD_LAYERS.FILL,
  SITEID_LAYERS.POLYGONS,
  SITEID_LAYERS.CLUSTERS,
];

// True when a higher-priority feature layer has a feature under the click.
// Layers absent from the current product are filtered out first —
// queryRenderedFeatures throws on unknown layer ids.
export function hasClickPriorityHit(map, point) {
  const present = CLICK_PRIORITY_LAYERS.filter(id => map.getLayer(id));
  if (!present.length) return false;
  return map.queryRenderedFeatures(point, { layers: present }).length > 0;
}
